import { motion } from "framer-motion";
import { ArrowRight, Handshake } from "lucide-react";
import Lightfall from "@/components/Lightfall";

const CTABand = ({
  layout = "split",
  eyebrow,
  title = "Get Started with Next-Gen AI Solutions",
  description = "Embark on a transformative journey with our next-generation AI solutions designed to revolutionize your business operations.",
  ctaLabel = "Get Started",
  ctaHref = "/contact",
}) => {
  if (layout === "centered") {
    return (
      <section data-testid="cta-band" className="relative py-24 lg:py-20 bg-miti-navy overflow-hidden hero-grain">
        <div className="absolute inset-0">
          <Lightfall
            colors={["#A6C8FF", "#5227FF", "#FF9FFC"]}
            backgroundColor="#0A192F"
            speed={0.4}
            streakCount={2}
            streakWidth={1}
            streakLength={1}
            density={0.5}
            twinkle={1}
            glow={1}
            backgroundGlow={0.4}
            zoom={3}
            opacity={0.9}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          {eyebrow && (
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="block text-xs font-semibold tracking-[0.25em] uppercase text-sky-300"
            >
              {eyebrow}
            </motion.span>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight"
          >
            {title}
          </motion.h2>
          <div className="mt-6 mx-auto h-0.5 w-12 bg-miti-blue" />
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed"
          >
            {description}
          </motion.p>
          <motion.a
            href={ctaHref}
            data-testid="cta-contact-button"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="group mt-9 inline-flex items-center gap-3 rounded-full border border-white/40 px-8 py-4 text-sm font-semibold text-white hover:bg-white hover:text-miti-navy transition-all duration-300"
          >
            {ctaLabel}
            <Handshake size={16} />
          </motion.a>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="cta-band" className="relative py-24 lg:py-16 bg-miti-navy overflow-hidden hero-grain">
      <div className="absolute inset-0">
        <img src="/assets/cta-bg.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-miti-navy/80" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            {title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-300">{description}</p>
        </motion.div>
        <motion.a
          href={ctaHref}
          data-testid="cta-get-started-button"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-miti-blue px-8 py-4 text-sm font-semibold text-white hover:bg-sky-400 hover:text-miti-navy transition-all duration-300"
        >
          {ctaLabel}
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </motion.a>
      </div>
    </section>
  );
};

export default CTABand;
